/**
 * constants/feeEnums.ts
 * Fee calculation domain constants and display data
 *
 * Provides runtime constants for the FeeEnums domain including:
 * - Display maps for all 9 fee-related union types
 *
 * These constants correspond to the pure type definitions in `src/types/shared/FeeEnums.ts`.
 *
 * RUNTIME ENUM AUDIT RATIONALE:
 * - All 9 FeeEnums use literal unions + as const maps pattern (no runtime enums)
 * - No external library, serialization, or cross-boundary requirement exists for runtime enum objects
 * - See Task 5 Phase 2 documentation for detailed audit results
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - This module may only `import type` from `@/types/shared/FeeEnums`
 * - No imports from `utils/` or other domain constants
 * - No side effects, pure data only
 * - All exports use `as const satisfies` for compile-time type safety
 *
 * TESTING GUIDELINES:
 * - Do NOT import from this file in tests to assert behavior
 * - Prefer importing parametric utils and passing these maps as inputs
 * - This keeps tests decoupled from constants and focused on logic
 * - Phase 4 will include vitest assertions that map keys are A→Z by token (no runtime side effects)
 *
 * ESLINT RULE MAINTENANCE:
 * - When adding new display maps, update ESLint 'no-restricted-imports' rule
 * - in both main config (line ~123) and services override (line ~195) in eslint.config.js
 *
 * See also:
 * - src/constants/assetClass.ts - Canonical example for display maps pattern
 * - src/constants/incrementType.ts - Canonical example for exhaustiveness checks
 */

// Internal absolute imports (type-only)
import type {
  CommissionTiming,
  CommissionType,
  ConversionRateType,
  ConversionTrigger,
  FeeComponent,
  OvernightAssetClass,
  OvernightBenchmark,
  OvernightFrequency,
  VerificationStatus
} from '@/types/shared/FeeEnums';

// --- DISPLAY MAPS ---

// keep alphabetical by key for drift hygiene
/** Display names for commission calculation types */
export const COMMISSION_TYPE_DISPLAY_MAP = {
  fixedPerTransaction: 'Fixed Per Transaction',
  fixedPerUnit: 'Fixed Per Unit',
  makerTaker: 'Maker/Taker',
  percentage: 'Percentage',
  perShare: 'Per Share',
  perSharePlusBase: 'Per Share + Base',
  spreadOnly: 'Spread Only',
  tiered: 'Tiered'
} as const satisfies Readonly<Record<CommissionType, string>>;

// keep alphabetical by key for drift hygiene
/** Display names for commission timing options */
export const COMMISSION_TIMING_DISPLAY_MAP = {
  bothLegs: 'Both Legs',
  perSide: 'Per Side',
  roundTrip: 'Round Trip'
} as const satisfies Readonly<Record<CommissionTiming, string>>;

// keep alphabetical by key for drift hygiene
/**
 * Display names for overnight financing benchmarks (upper-case per financial convention)
 *
 * Note: display values intentionally UPPER CASE to match market convention (SOFR, SONIA, etc.)
 */
export const OVERNIGHT_BENCHMARK_DISPLAY_MAP = {
  custom: 'Custom',
  estr: 'ESTR',
  libor: 'LIBOR',
  sofr: 'SOFR',
  sonia: 'SONIA',
  tndr: 'TNDR',
  tonar: 'TONAR'
} as const satisfies Readonly<Record<OvernightBenchmark, string>>;

// keep alphabetical by key for drift hygiene
/** Display names for overnight financing frequency options */
export const OVERNIGHT_FREQUENCY_DISPLAY_MAP = {
  daily: 'Daily',
  tripleFriday: 'Triple Friday',
  tripleWednesday: 'Triple Wednesday',
  weekdayOnly: 'Weekday Only'
} as const satisfies Readonly<Record<OvernightFrequency, string>>;

// keep alphabetical by key for drift hygiene
/** Display names for overnight financing asset class categories */
export const OVERNIGHT_ASSET_CLASS_DISPLAY_MAP = {
  commodities: 'Commodities',
  crypto: 'Crypto',
  forex: 'Forex',
  indices: 'Indices',
  stocks: 'Stocks'
} as const satisfies Readonly<Record<OvernightAssetClass, string>>;

// keep alphabetical by key for drift hygiene
/** Display names for currency conversion trigger events */
export const CONVERSION_TRIGGER_DISPLAY_MAP = {
  commission: 'Commission',
  deposit: 'Deposit',
  trading: 'Trading',
  withdrawal: 'Withdrawal'
} as const satisfies Readonly<Record<ConversionTrigger, string>>;

// keep alphabetical by key for drift hygiene
/** Display names for currency conversion rate types */
export const CONVERSION_RATE_TYPE_DISPLAY_MAP = {
  fixed: 'Fixed',
  spot: 'Spot',
  spotPlusMarkup: 'Spot + Markup',
  undisclosed: 'Undisclosed'
} as const satisfies Readonly<Record<ConversionRateType, string>>;

// keep alphabetical by key for drift hygiene
/** Display names for fee data verification status */
export const VERIFICATION_STATUS_DISPLAY_MAP = {
  assumed: 'Assumed',
  template: 'Template',
  userOverride: 'User Override',
  userVerified: 'User Verified',
  verified: 'Verified'
} as const satisfies Readonly<Record<VerificationStatus, string>>;

// keep alphabetical by key for drift hygiene
/** Display names for fee component types */
export const FEE_COMPONENT_DISPLAY_MAP = {
  commission: 'Commission',
  conversion: 'Conversion',
  overnight: 'Overnight'
} as const satisfies Readonly<Record<FeeComponent, string>>;

// --- COMPILE-TIME EXHAUSTIVENESS CHECKS ---

/**
 * @internal
 * Compile-time coverage checks only - do NOT re-export from barrels.
 */

type _AllCommissionTypeCovered =
  Exclude<CommissionType, keyof typeof COMMISSION_TYPE_DISPLAY_MAP> extends never ? true : never;
export type _CommissionTypeChecks = _AllCommissionTypeCovered;

type _AllCommissionTimingCovered =
  Exclude<CommissionTiming, keyof typeof COMMISSION_TIMING_DISPLAY_MAP> extends never ? true : never;
export type _CommissionTimingChecks = _AllCommissionTimingCovered;

type _AllOvernightBenchmarkCovered =
  Exclude<OvernightBenchmark, keyof typeof OVERNIGHT_BENCHMARK_DISPLAY_MAP> extends never ? true : never;
export type _OvernightBenchmarkChecks = _AllOvernightBenchmarkCovered;

type _AllOvernightFrequencyCovered =
  Exclude<OvernightFrequency, keyof typeof OVERNIGHT_FREQUENCY_DISPLAY_MAP> extends never ? true : never;
export type _OvernightFrequencyChecks = _AllOvernightFrequencyCovered;

type _AllOvernightAssetClassCovered =
  Exclude<OvernightAssetClass, keyof typeof OVERNIGHT_ASSET_CLASS_DISPLAY_MAP> extends never ? true : never;
export type _OvernightAssetClassChecks = _AllOvernightAssetClassCovered;

type _AllConversionTriggerCovered =
  Exclude<ConversionTrigger, keyof typeof CONVERSION_TRIGGER_DISPLAY_MAP> extends never ? true : never;
export type _ConversionTriggerChecks = _AllConversionTriggerCovered;

type _AllConversionRateTypeCovered =
  Exclude<ConversionRateType, keyof typeof CONVERSION_RATE_TYPE_DISPLAY_MAP> extends never ? true : never;
export type _ConversionRateTypeChecks = _AllConversionRateTypeCovered;

type _AllVerificationStatusCovered =
  Exclude<VerificationStatus, keyof typeof VERIFICATION_STATUS_DISPLAY_MAP> extends never ? true : never;
export type _VerificationStatusChecks = _AllVerificationStatusCovered;

type _AllFeeComponentCovered =
  Exclude<FeeComponent, keyof typeof FEE_COMPONENT_DISPLAY_MAP> extends never ? true : never;
export type _FeeComponentChecks = _AllFeeComponentCovered;
